import { create } from 'zustand';
import type { User } from 'firebase/auth';

/* Who is signed in, and whether they may be here.
 *
 * The gate is a single Firebase custom claim, `superAdmin: true` — the same
 * predicate db/firestore.IsSuperAdmin reads, so the browser and any future Go
 * middleware agree on one thing rather than two (DR-25). */

export type AuthPhase = 'unconfigured' | 'loading' | 'signedOut' | 'signedIn';

export interface AuthState {
  phase: AuthPhase;
  user: User | null;
  email: string | null;
  displayName: string | null;
  isSuperAdmin: boolean;
  error: string | null;

  setUnconfigured(): void;
  setSignedOut(): void;
  setSignedIn(user: User, isSuperAdmin: boolean): void;
  setError(message: string | null): void;
}

export const useAuthStore = create<AuthState>((set) => ({
  phase: 'loading',
  user: null,
  email: null,
  displayName: null,
  isSuperAdmin: false,
  error: null,

  setUnconfigured: () =>
    set({ phase: 'unconfigured', user: null, email: null, isSuperAdmin: false }),

  setSignedOut: () =>
    set({
      phase: 'signedOut',
      user: null,
      email: null,
      displayName: null,
      isSuperAdmin: false,
    }),

  setSignedIn: (user, isSuperAdmin) =>
    set({
      phase: 'signedIn',
      user,
      email: user.email,
      displayName: user.displayName,
      isSuperAdmin,
      error: null,
    }),

  setError: (error) => set({ error }),
}));
